/**
 * RainSkipNotice — which valves rain left dry today, and why.
 *
 * Without it a block skipped for rain looks exactly like one whose program
 * failed: scheduled, and nothing open. One line per run, naming the valves,
 * how much fell or is forecast, over how long, and where the figure came from
 * (the farm's station, Open-Meteo's model, or its forecast). On a bridge too
 * old to apply rain skips the valves watered anyway, and the notice says so
 * instead.
 */

import { CloudRain, AlertTriangle } from 'lucide-react';
import type { RainSkipReason, RainSource } from '../../services/espnowIrrigation';
import { formatTimeInTz } from '../../utils/timezone';
import { reasonText, sourceText } from '../../utils/rainSkip';

export interface RainSkipItem {
  program_name: string | null;
  starts_at: string;
  router_mac: string;
  relay_index: number;
  /** Rain that fell, or rain forecast. Absent means fallen. */
  reason?: RainSkipReason;
  rain_mm: number;
  hours: number;
  source: RainSource | null;
  station_name?: string | null;
  /** False when an old bridge ran the valve anyway. Absent means applied. */
  applied?: boolean;
}

interface Props {
  skips: RainSkipItem[];
  orgTimezone: string;
  nameOf: (routerMac: string, relayIndex: number) => string;
  /** Prefix naming the site, where a farm has more than one. */
  siteName?: string | null;
}

interface RunGroup {
  key: string;
  program: string;
  startsAt: string;
  applied: boolean;
  valves: string[];
  first: RainSkipItem;
}

export default function RainSkipNotice({ skips, orgTimezone, nameOf, siteName }: Props) {
  if (!skips || skips.length === 0) return null;

  const groups = new Map<string, RunGroup>();
  for (const s of skips) {
    const applied = s.applied !== false;
    const key = `${s.program_name ?? ''}|${s.starts_at}|${applied}|${s.reason ?? 'fallen'}`;
    const g = groups.get(key) ?? {
      key, program: s.program_name || 'A program', startsAt: s.starts_at, applied, valves: [], first: s,
    };
    g.valves.push(nameOf(s.router_mac, s.relay_index));
    groups.set(key, g);
  }

  return (
    <div className="space-y-1.5" aria-label="Rain skips today">
      {[...groups.values()].map(g => {
        const when = formatTimeInTz(g.startsAt, orgTimezone);
        const why = `${reasonText(g.first.reason, g.first.rain_mm, g.first.hours)} (${sourceText(g.first.source, g.first.station_name)})`;
        return g.applied ? (
          <div key={g.key} className="flex items-start gap-2 p-2.5 rounded-lg bg-sky-50 border border-sky-200 text-xs text-sky-900">
            <CloudRain className="w-4 h-4 mt-px flex-shrink-0 text-sky-600" aria-hidden />
            <p className="min-w-0 break-words">
              {siteName && <span className="font-medium">{siteName}: </span>}
              <span className="font-medium">
                {g.first.reason === 'forecast' ? 'Skipped, rain forecast' : 'Skipped for rain'}
              </span> · {g.program} at {when}:{' '}
              {g.valves.join(', ')}. {why}.
            </p>
          </div>
        ) : (
          <div key={g.key} className="flex items-start gap-2 p-2.5 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-900">
            <AlertTriangle className="w-4 h-4 mt-px flex-shrink-0 text-amber-600" aria-hidden />
            <p className="min-w-0 break-words">
              {siteName && <span className="font-medium">{siteName}: </span>}
              {g.program} at {when} should have skipped {g.valves.join(', ')} ({why}), but this site's
              bridge is too old to skip valves, so they watered. Update the bridge on the Settings tab.
            </p>
          </div>
        );
      })}
    </div>
  );
}
